import { defineStore } from 'pinia';
import { type Game, defaultGame } from '../models/game';
import { type GameShareClient } from '../models/gameShare';
import { Firestore, Timestamp, collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

/**
 * Safely extract milliseconds from a Firestore Timestamp or a serialized
 * plain object {seconds, nanoseconds}. Returns 0 for null/undefined.
 */
function toMillis(ts: Timestamp | { seconds: number; nanoseconds: number } | null | undefined): number {
	if (!ts) return 0;
	if (typeof (ts as Timestamp).toMillis === 'function') return (ts as Timestamp).toMillis();
	if ('seconds' in ts) return ts.seconds * 1000 + Math.floor((ts.nanoseconds || 0) / 1e6);
	return 0;
}

export interface GameStoreState {
	games: Game[];
	gameShares: GameShareClient[];
	selectedGame: Game | null;
	loading: boolean;
	openDialogGame: boolean;
}

export const useGameStore = defineStore('GameStore', {
	state: (): GameStoreState => ({
		games: [],
		gameShares: [],
		selectedGame: null,
		loading: false,
		openDialogGame: false,
	}),

	getters: {
		myGames: (state) => state.games,
		upcomingGames: (state) => {
			const now = Date.now();
			return state.games
				.filter(g => g.startTime && toMillis(g.startTime) > now)
				.sort((a, b) => toMillis(a.startTime) - toMillis(b.startTime));
		},
		pastGames: (state) => {
			const now = Date.now();
			return state.games
				.filter(g => g.startTime && toMillis(g.startTime) <= now)
				.sort((a, b) => toMillis(b.startTime) - toMillis(a.startTime));
		},
	},

	actions: {
		async fetchGameShares() {
			const userStore = useUserStore();
			if (!userStore.isLoggedIn) return;

			const { $firestore } = useNuxtApp();
			const snap = await getDocs(
				collection($firestore as Firestore, `users/${userStore.id}/game-shares`)
			);

			this.gameShares = snap.docs.map(d => ({
				shareId: d.id,
				...d.data(),
			} as GameShareClient));
		},

		async fetchMyGames() {
			const userStore = useUserStore();
			if (!userStore.isLoggedIn) return;

			this.loading = true;
			await this.fetchGameShares();

			const { $firestore } = useNuxtApp();
			const games: Game[] = [];

			// Read each game doc individually (getDoc works with playerIds rule)
			for (const share of this.gameShares) {
				try {
					const snap = await getDoc(doc($firestore as Firestore, 'games', share.gameId));
					if (snap.exists()) {
						games.push({ ...snap.data(), id: snap.id } as Game);
					}
				} catch (e) {
					console.warn('Could not load game:', share.gameId);
				}
			}

			this.games = games;
			this.loading = false;
		},

		async createGame(gameData: Omit<Game, 'id' | 'createdAt' | 'createdBy' | 'createdByAlias' | 'players'>, creatorRole: string = 'gm'): Promise<string | null> {
			const functions = getFunctions();
			const create = httpsCallable<object, { success: boolean; gameId: string }>(functions, 'createGame');
			const result = await create({ ...gameData, creatorRole });
			await this.fetchMyGames();
			return result.data.gameId;
		},

		async updateGame(gameId: string, fields: Partial<Game>): Promise<void> {
			const functions = getFunctions();
			const update = httpsCallable<object, { success: boolean }>(functions, 'updateGame');
			await update({ gameId, ...fields });
			await this.fetchMyGames();
		},

		async deleteGame(gameId: string): Promise<void> {
			const functions = getFunctions();
			const del = httpsCallable<{ gameId: string }, { success: boolean }>(functions, 'deleteGame');
			await del({ gameId });
			this.games = this.games.filter(g => g.id !== gameId);
			this.gameShares = this.gameShares.filter(s => s.gameId !== gameId);
		},

		async leaveGame(shareId: string): Promise<void> {
			const functions = getFunctions();
			const leave = httpsCallable<{ shareId: string }, { success: boolean }>(functions, 'leaveGame');
			await leave({ shareId });
			const share = this.gameShares.find(s => s.shareId === shareId);
			if (share) {
				this.games = this.games.filter(g => g.id !== share.gameId);
				this.gameShares = this.gameShares.filter(s => s.shareId !== shareId);
			}
		},

		showDialogGame(game?: Game) {
			this.selectedGame = game || defaultGame();
			this.openDialogGame = true;
		},

		// Open the Create Game wizard prefilled for a campaign module. The
		// DialogGame wizard reads `moduleId` off the selected game on open and
		// applies that module's prefill + character-creation gating.
		showDialogGameForModule(moduleId: string) {
			this.selectedGame = { ...defaultGame(), moduleId };
			this.openDialogGame = true;
		},

		async removePlayer(gameId: string, alias: string): Promise<void> {
			const functions = getFunctions();
			const kick = httpsCallable<{ gameId: string; alias: string }, { success: boolean }>(functions, 'removePlayer');
			await kick({ gameId, alias });
			await this.fetchMyGames();
		},

		hideDialogGame() {
			this.openDialogGame = false;
			this.selectedGame = null;
		},
	},
});
